import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  Avatar,
  Row,
  Col,
  Typography,
  Space,
  Select,
  message,
  Tag
} from "antd";
import { PhoneOutlined, UserOutlined, IdcardOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Country, State, City } from "country-state-city";
import useSelfLearnerProfile from "PlatForms/SelfLMS/Hooks/SelfLearner/useSelfLearnerProfile";
import { ProfileContext } from "Common/hooks/context/ProfileContext";
import { toast } from "react-toastify";
import { CareerLevelEnum } from "Common/Constants/Enums/Individual/CareerLevelEnum";
import { ProfessionTypeEnum } from "Common/Constants/Enums/Individual/ProfessionTypeEnum";

const { Text } = Typography;
const { Option } = Select;

const SelfLearnerProfile = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { isProfileComplete, fetchProfileStatus } = React.useContext(ProfileContext);

  // Country, State, City States
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  // Enum data lists
  const [educationLevels, setEducationLevels] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [careerLevels, setCareerLevels] = useState([]);
  
  // State for custom text fields
  const [showProfessionOther, setShowProfessionOther] = useState(false);
  const [showCareerLevelOther, setShowCareerLevelOther] = useState(false);

  const {
    profileData,
    loading,
    updating,
    imageUrl,
    fileList,
    beforeUpload,
    handleImageChange,
    removeProfilePicture,
    removingImage,
    updateProfile,
    getEducationLevelList,
    getCareerLevelList,
    getProfessionTypeList,
  } = useSelfLearnerProfile(form);

  useEffect(() => {
    // Initialize countries
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);

    // Initialize enum lists
    setEducationLevels(getEducationLevelList());
    setProfessions(getProfessionTypeList());
    setCareerLevels(getCareerLevelList());
  }, [getEducationLevelList, getCareerLevelList, getProfessionTypeList]);

  // Handle profession change
  const handleProfessionChange = (value) => {
    // Check if "Other" is selected using the enum value
    const isOther = value === ProfessionTypeEnum.Other;
    setShowProfessionOther(isOther);
    
    if (!isOther) {
      form.setFieldsValue({ professionText: undefined });
    }
  };

  // Handle career level change
  const handleCareerLevelChange = (value) => {
    // Check if "Other" is selected using the enum value
    const isOther = value === CareerLevelEnum.Other;
    setShowCareerLevelOther(isOther);
    
    if (!isOther) {
      form.setFieldsValue({ careerLevelText: undefined });
    }
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    const relatedStates = State.getStatesOfCountry(value);
    setStates(relatedStates);
    setCities([]);
    form.setFieldsValue({ state: undefined, city: undefined });
  };

  const handleStateChange = (value) => {
    setSelectedState(value);
    const relatedCities = City.getCitiesOfState(selectedCountry, value);
    setCities(relatedCities);
    form.setFieldsValue({ city: undefined });
  };

  // Set form initial values when profileData is available
  useEffect(() => {
    if (profileData && !loading) {
      
      // Check if profession is "Other" using the enum value
      const isProfessionOther = profileData.profession === ProfessionTypeEnum.Other;
      setShowProfessionOther(isProfessionOther);
      
      // Check if career level is "Other" using the enum value
      const isCareerLevelOther = profileData.careerLevel === CareerLevelEnum.Other;
      setShowCareerLevelOther(isCareerLevelOther);

      form.setFieldsValue({
        firstName: profileData.firstName || "",
        lastName: profileData.lastName || "",
        phoneNo: profileData.phoneNo || "",
        educationLevel: profileData.educationLevel,
        profession: profileData.profession,
        careerLevel: profileData.careerLevel,
        professionText: profileData.professionText,
        careerLevelText: profileData.careerLevelText,
        country: profileData.country,
        state: profileData.state,
        city: profileData.city,
      });

      // If country exists in profile, load its states
      if (profileData.country) {
        setSelectedCountry(profileData.country);
        const relatedStates = State.getStatesOfCountry(profileData.country);
        setStates(relatedStates);

        // If state exists in profile, load its cities
        if (profileData.state) {
          setSelectedState(profileData.state);
          const relatedCities = City.getCitiesOfState(profileData.country, profileData.state);
          setCities(relatedCities);
        }
      }
    }
  }, [profileData, loading, form]);

  // Handle form values change to detect when profession or career level changes
  const onValuesChange = (changedValues) => {
    if (changedValues.profession !== undefined) {
      handleProfessionChange(changedValues.profession);
    }
    if (changedValues.careerLevel !== undefined) {
      handleCareerLevelChange(changedValues.careerLevel);
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      
      // Ensure all required fields are included
      const formValues = {
        ...values,
        // Make sure profession field is correctly named
        profession: values.profession,
        educationLevel: values.educationLevel,
        careerLevel: values.careerLevel,
        // Include custom text fields if "Other" is selected
        professionText: showProfessionOther ? values.professionText : "",
        careerLevelText: showCareerLevelOther ? values.careerLevelText : "",
      };


      const success = await updateProfile(formValues, navigate);

      // Check if profile is complete after update
      if (success) {
        toast.success("Profile updated successfully!");

        // Fetch the latest profile status
        await fetchProfileStatus();

        // Navigate to dashboard
        setTimeout(() => {
          navigate("/selfLearner-dashboard");
        }, 1000);
      } else {
        message.error("Failed to update profile");
      }
    } catch (error) {
      console.error("Error in handleFormSubmit:", error);
      toast.error(error || "An error occurred while updating your profile");
    }
  };

  return (
   <Card
  loading={loading}
  title="Profile"
  className="card-professional"
>
      <Form 
        form={form} 
        layout="vertical" 
        onFinish={handleFormSubmit}
        onValuesChange={onValuesChange}
      >
        <Row gutter={[32, 16]} align="middle">
  <Col xs={24} md={6} style={{ textAlign: "center" }}>
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      height: '100%'
    }}>
      {/* Learner ID Section - Beside the image */}
      <div style={{ 
        marginBottom: 20, 
        textAlign: 'center',
        width: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginBottom: 8 
        }}>
                  <Text strong style={{ fontSize: '14px' }}>Learner ID</Text>
        </div>
        <Tag 
          color="gold" 
          style={{ 
            fontSize: '14px', 
            fontWeight: 'bold',
            padding: '4px 12px',
            borderRadius: '4px',
            marginBottom: 4
          }}
        >
          {profileData?.learnerCode || 'N/A'}
        </Tag>
        <div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            Unique identification
          </Text>
        </div>
      </div>
      
      {/* Profile Image Section */}
      <Form.Item style={{ marginTop: 'auto' }}>
        <Upload
          name="imageFile"
          listType="picture-card"
          showUploadList={false}
          beforeUpload={beforeUpload}
          onChange={handleImageChange}
          fileList={fileList}
          accept="image/*"
        >
          {imageUrl ? (
            <Avatar src={imageUrl} size={110} style={{ border: "2px solid #eee" }} />
          ) : (
            <Avatar
              icon={<UserOutlined />}
              size={110}
              style={{ backgroundColor: "#f0f0f0" }}
            />
          )}
        </Upload>
        <Text type="secondary">Click to change</Text>
        <Text type="secondary" style={{ display: "block", fontSize: 11.5, marginTop: 2, color: "#78716C" }}>
          JPG or PNG, up to 1MB
        </Text>
        {imageUrl && (
          <div style={{ marginTop: 6 }}>
            <Button
              type="link"
              danger
              size="small"
              onClick={removeProfilePicture}
              loading={removingImage}
              style={{ padding: 0, height: "auto", fontSize: 13 }}
            >
              Remove photo
            </Button>
          </div>
        )}
      </Form.Item>
    </div>
  </Col>
   <Col xs={24} md={18}>
            <Row gutter={16}>
          

              <Col xs={24} md={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[{ required: true, message: "Please input your first name!" }]}
                >
                  <Input placeholder="John" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[{ required: true, message: "Please input your last name!" }]}
                >
                  <Input placeholder="Doe" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Email">
                  <Input value={profileData?.email || ""} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Phone Number"
                  name="phoneNo"
                  rules={[
                    { required: true, message: "Please enter your phone number!" },
                    {
                      pattern: /^\+?[0-9\s]*$/,
                      message: "Please enter only numbers and optional + prefix!",
                    },
                  ]}
                >
                  <Input
                    type="tel"
                    prefix={<PhoneOutlined />}
                    placeholder="+1234567890"
                    onKeyPress={(e) => {
                      if (!/[0-9+]/.test(e.key) && e.key !== "Backspace") e.preventDefault();
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      if ((value.match(/\+/g) || []).length > 1) {
                        e.target.value = value.replace(/\+/g, "");
                        if (value.startsWith("+")) e.target.value = "+" + e.target.value;
                      }
                    }}
                  />
                </Form.Item>
              </Col>

              {/* Education Level */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Education Level"
                  name="educationLevel"
                  rules={[{ required: true, message: "Please select your education level!" }]}
                >
                  <Select
                    placeholder="Select education level"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {educationLevels.map((level) => (
                      <Option key={level.value} value={level.value}>
                        {level.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* Profession */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Profession"
                  name="profession"
                  rules={[{ required: true, message: "Please select your profession!" }]}
                >
                  <Select
                    placeholder="Select profession"
                    showSearch
                    optionFilterProp="children"
                    onChange={handleProfessionChange}
                    filterOption={(input, option) =>
                      option?.children?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {professions.map((profession) => (
                      <Option key={profession.value} value={profession.value}>
                        {profession.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* Custom Profession Text (shown when "Other" is selected) */}
              {showProfessionOther && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Please specify your profession"
                    name="professionText"
                    rules={[{ required: showProfessionOther, message: "Please specify your profession!" }]}
                  >
                    <Input placeholder="Enter your profession" />
                  </Form.Item>
                </Col>
              )}

              {/* Career Level */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Career Level"
                  name="careerLevel"
                  rules={[{ required: true, message: "Please select your career level!" }]}
                >
                  <Select
                    placeholder="Select career level"
                    showSearch
                    optionFilterProp="children"
                    onChange={handleCareerLevelChange}
                    filterOption={(input, option) =>
                      option?.children?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {careerLevels.map((level) => (
                      <Option key={level.value} value={level.value}>
                        {level.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* Custom Career Level Text (shown when "Other" is selected) */}
              {showCareerLevelOther && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Please specify your career level"
                    name="careerLevelText"
                    rules={[{ required: showCareerLevelOther, message: "Please specify your career level!" }]}
                  >
                    <Input placeholder="Enter your career level" />
                  </Form.Item>
                </Col>
              )}

              {/* Country */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Country"
                  name="country"
                  rules={[{ required: true, message: "Please select your country!" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select your country"
                    optionFilterProp="children"
                    onChange={handleCountryChange}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={countries.map((country) => ({
                      label: country.name,
                      value: country.isoCode,
                    }))}
                  />
                </Form.Item>
              </Col>

              {/* State */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="State"
                  name="state"
                  rules={[
                    {
                      required: selectedCountry === "US",
                      message: "State is required when country is United States!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select your state"
                    optionFilterProp="children"
                    onChange={handleStateChange}
                    disabled={!selectedCountry}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={states.map((state) => ({
                      label: state.name,
                      value: state.isoCode,
                    }))}
                  />
                </Form.Item>
              </Col>

              {/* City */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="City"
                  name="city"
                  rules={[{ required: cities.length > 0, message: "Please select your city!" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select your city"
                    optionFilterProp="children"
                    disabled={!selectedState}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={cities.map((city) => ({
                      label: city.name,
                      value: city.name,
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={updating}>
                      Update Profile
                    </Button>
                    <Button
                      onClick={() => {
                        form.resetFields();
                        // Re-set initial values from profile data
                        if (profileData) {
                          form.setFieldsValue({
                            firstName: profileData.firstName,
                            lastName: profileData.lastName,
                            phoneNo: profileData.phoneNo,
                            educationLevel: profileData.educationLevel,
                            profession: profileData.profession,
                            careerLevel: profileData.careerLevel,
                            professionText: profileData.professionText,
                            careerLevelText: profileData.careerLevelText,
                            country: profileData.country,
                            state: profileData.state,
                            city: profileData.city,
                          });
                          
                          // Set the visibility of custom text fields
                          setShowProfessionOther(profileData.profession === ProfessionTypeEnum.Other);
                          setShowCareerLevelOther(profileData.careerLevel === CareerLevelEnum.Other);
                        }
                      }}
                      disabled={updating}
                    >
                      Reset
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default SelfLearnerProfile;